import clsx from "clsx";
import styles from "./MailBarcode.module.css";

/**
 * A four-state postal barcode — the kind sorting machines read off the bottom
 * of an envelope: every bar is a tracker, an ascender, a descender or a full
 * bar.
 *
 * The bars are derived from the string passed in (a message ID, a build
 * version), so the same input always draws the same mark and two messages
 * never look alike. It encodes nothing a machine could read back: it is a
 * fingerprint of the value printed beside it, not a claim to be postal data.
 */

const BARS = 65; // as many as an Intelligent Mail barcode carries
const PITCH = 3;
const BAR_WIDTH = 1.5;
const HEIGHT = 18;
const MID_TOP = 6.5;
const MID_BOTTOM = 11.5;

/** FNV-1a, then a linear congruential walk — stable across browsers. */
function bars(value: string): { y: number; height: number }[] {
  let h = 2166136261;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  const out: { y: number; height: number }[] = [];
  for (let b = 0; b < BARS; b++) {
    h = (Math.imul(h, 1103515245) + 12345) >>> 0;
    const state = (h >>> 16) & 3; // 0 tracker · 1 ascender · 2 descender · 3 full
    const y = state === 1 || state === 3 ? 0 : MID_TOP;
    const bottom = state === 2 || state === 3 ? HEIGHT : MID_BOTTOM;
    out.push({ y, height: bottom - y });
  }
  return out;
}

export interface MailBarcodeProps {
  /** The value the bars are derived from. */
  value: string;
  /** Accessible name; omit to hide the mark from assistive tech. */
  label?: string;
  className?: string;
}

export function MailBarcode({ value, label, className }: MailBarcodeProps) {
  return (
    <svg
      viewBox={`0 0 ${BARS * PITCH - (PITCH - BAR_WIDTH)} ${HEIGHT}`}
      preserveAspectRatio="xMinYMid meet"
      className={clsx(styles.barcode, className)}
      role={label ? "img" : "presentation"}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <g fill="currentColor">
        {bars(value).map((bar, i) => (
          <rect key={i} x={i * PITCH} y={bar.y} width={BAR_WIDTH} height={bar.height} />
        ))}
      </g>
    </svg>
  );
}
